import React, { useState, useEffect, useMemo, useRef } from 'react';
import { apiService, graphBuilderApi } from '../../services/api';
import {
  LLM_MODEL_OPTIONS as STATIC_LLM_MODEL_OPTIONS,
  STAGE_MODEL_KEYS,
  MODEL_PRESETS,
  CONTEXT_POTENCY_MODE_OPTIONS,
  PIPELINE_TUNING_PRESETS,
} from '../../constants/pipelineSettings';

const PROVIDER_TYPE_LABELS = {
  openai: 'OpenAI',
  do_serverless: 'DO Serverless',
  do_droplet: 'DO Droplet',
  do_dedicated: 'DO Dedicated',
};

const ENGINE_MODES = [
  { value: 'compiled_full', label: 'Compiled Full (Default)' },
  { value: 'compiled_full_numpy', label: 'Compiled Full - Fast Engine' },
  { value: 'not_compiled', label: 'Not Compiled (Debug)' },
  { value: 'compiled_sharded', label: 'Compiled Sharded (Legacy)' },
];

const EXECUTION_MODES = [
  { value: 'BUILD_AND_RUN', label: 'Build & Run (Full Pipeline)' },
  { value: 'BUILD_ONLY', label: 'Build Only (No LLM Response)' },
];

const TestGraphSettings = ({ 
  settings, 
  onSettingsChange, 
  collapsed, 
  onToggleCollapse,
  graphId,
  workspaceId,
  cacheInfo: cacheInfoProp,
  onCacheStateChange,
}) => {
  const [vocabWarning, setVocabWarning] = useState('');
  const [compileModeWarning, setCompileModeWarning] = useState('');
  const [snnTuningOpen, setSnnTuningOpen] = useState(false);
  const [stageModelsOpen, setStageModelsOpen] = useState(false);
  const [mlcfOpen, setMlcfOpen] = useState(false);
  const [crsOpen, setCrsOpen] = useState(false);
  const [serOpen, setSerOpen] = useState(false);
  const [routerGraphs, setRouterGraphs] = useState([]);
  const [llmProviders, setLlmProviders] = useState([]);
  const [processMaps, setProcessMaps] = useState([]);
  const [processDropdownOpen, setProcessDropdownOpen] = useState(false);
  const processDropdownRef = useRef(null);

  const LLM_MODEL_OPTIONS = useMemo(() => {
    // --- STATIC FALLBACK DISABLED: only DB-configured providers are shown ---
    // if (!llmProviders.length) return STATIC_LLM_MODEL_OPTIONS;
    const providerOpts = llmProviders.map((p) => ({
      value: `provider::${p.id}::${p.model_name}`,
      label: `${p.model_name} (${PROVIDER_TYPE_LABELS[p.provider_type] || p.provider_type} - ${p.name})`,
      provider_id: p.id,
      model_name: p.model_name,
      is_default: p.is_default,
    }));
    return providerOpts;
    // --- STATIC FALLBACK DISABLED ---
    // return [...providerOpts, ...STATIC_LLM_MODEL_OPTIONS];
  }, [llmProviders]);

  // Auto-select the default provider on mount if no explicit selection saved,
  // or reconcile stale provider::uuid::model values against current DB state.
  useEffect(() => {
    if (!llmProviders.length) return;
    const defaultProvider = llmProviders.find((p) => p.is_default) || llmProviders[0];
    if (defaultProvider && !settings.llm_model?.startsWith('provider::')) {
      const providerValue = `provider::${defaultProvider.id}::${defaultProvider.model_name}`;
      onSettingsChange('llm_model', providerValue);
    } else if (settings.llm_model?.startsWith('provider::')) {
      const parts = settings.llm_model.split('::');
      if (parts.length >= 3) {
        const savedId = parts[1];
        const savedModel = parts.slice(2).join('::');
        const matchedProvider = llmProviders.find((p) => p.id === savedId);
        if (matchedProvider && matchedProvider.model_name !== savedModel) {
          onSettingsChange('llm_model', `provider::${matchedProvider.id}::${matchedProvider.model_name}`);
        } else if (!matchedProvider && defaultProvider) {
          onSettingsChange('llm_model', `provider::${defaultProvider.id}::${defaultProvider.model_name}`);
        }
      }
    }
  }, [llmProviders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Graph cache state
  const [cacheStatus, setCacheStatus] = useState('idle'); // idle | caching | cached | error
  const [cacheInfo, setCacheInfo] = useState(null);
  const [cacheError, setCacheError] = useState('');
  const [deletingCache, setDeletingCache] = useState(false);
  const [cacheJobId, setCacheJobId] = useState(null);
  const [cachePhase, setCachePhase] = useState(null);
  const [cachePercent, setCachePercent] = useState(0);
  const cachePollRef = useRef(null);

  useEffect(() => {
    const loadRouterGraphs = async () => {
      try {
        const res = await apiService.listGraphs(1, 100);
        const routers = (res.graphs || []).filter(g => g.graphType === 'Router');
        setRouterGraphs(routers);
      } catch (err) {
        console.error('Failed to load router graphs:', err);
      }
    };
    loadRouterGraphs();
    apiService.listLLMProviders().then((d) => setLlmProviders(d.providers || [])).catch(() => {});
    graphBuilderApi.listProcessDefinitions({ status: 'active', limit: 100 })
      .then((d) => setProcessMaps(Array.isArray(d) ? d : d.definitions || d.processes || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (processDropdownRef.current && !processDropdownRef.current.contains(e.target)) {
        setProcessDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check cache status on mount / when graphId changes.
  // If the parent (TestGraphPage) already supplied cacheInfo via the
  // cacheInfoProp prop we hydrate from that and skip the duplicate
  // network call. Otherwise fall back to a self-probe so this component
  // remains usable in isolation.
  useEffect(() => {
    if (!graphId) return;
    if (cacheInfoProp !== undefined && cacheInfoProp !== null) {
      setCacheInfo(cacheInfoProp);
      if (cacheInfoProp.fully_cached) {
        setCacheStatus((prev) => (prev === 'caching' ? prev : 'cached'));
      }
      return;
    }
    const checkCache = async () => {
      try {
        const result = await graphBuilderApi.getCacheStatus(graphId);
        setCacheInfo(result);
        if (result.fully_cached) {
          setCacheStatus('cached');
        }
      } catch (err) {
        // Silently ignore — cache status is optional
      }
    };
    checkCache();
  }, [graphId, cacheInfoProp]);

  const stopCachePolling = () => {
    if (cachePollRef.current) {
      clearInterval(cachePollRef.current);
      cachePollRef.current = null;
    }
  };

  const startCachePolling = (jobId, startTime) => {
    stopCachePolling();
    cachePollRef.current = setInterval(async () => {
      try {
        const job = await graphBuilderApi.getCacheJob(jobId);
        setCachePhase(job.phase || null);
        setCachePercent(typeof job.percent === 'number' ? job.percent : 0);
        if (job.status === 'completed') {
          stopCachePolling();
          const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
          setCacheStatus('cached');
          setCacheInfo({ ...job, elapsed_s: elapsed });
          setCacheJobId(null);
          onSettingsChange('use_cache', true);
          if (typeof onCacheStateChange === 'function') {
            onCacheStateChange();
          }
        } else if (job.status === 'failed') {
          stopCachePolling();
          setCacheStatus('error');
          setCacheError(job.error || 'Cache job failed');
          setCacheJobId(null);
          if (typeof onCacheStateChange === 'function') {
            onCacheStateChange();
          }
        }
      } catch (err) {
        console.error('Cache job poll failed:', err);
        // 404 = job evicted from Redis (TTL expired or never persisted).
        if (err?.response?.status === 404) {
          stopCachePolling();
          setCacheStatus('error');
          setCacheError('Cache job state lost. Please retry.');
          setCacheJobId(null);
        }
      }
    }, 2000);
  };

  const handleCacheGraph = async () => {
    if (cacheStatus === 'caching') return;
    setCacheStatus('caching');
    setCacheError('');
    setCachePhase('queued');
    setCachePercent(0);
    const startTime = Date.now();
    try {
      // Extract provider_id for server-side resolution (no API key in browser)
      let providerId = undefined;
      if (settings.llm_model && settings.llm_model.startsWith('provider::')) {
        const parts = settings.llm_model.split('::');
        providerId = parts[1];
      }
      const submit = await graphBuilderApi.cacheGraphSubmit(graphId, {
        workspaceId,
        providerId,
      });
      setCacheJobId(submit.job_id);
      startCachePolling(submit.job_id, startTime);
    } catch (err) {
      // 409 = a cache job is already running for this graph; re-attach.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.existing_job_id) {
        setCacheJobId(detail.existing_job_id);
        setCachePhase(detail.phase || 'running');
        setCachePercent(detail.percent || 0);
        startCachePolling(detail.existing_job_id, startTime);
        return;
      }
      console.error('Cache graph submit failed:', err);
      setCacheStatus('error');
      setCacheError(detail?.message || detail || err.message || 'Caching failed');
    }
  };

  // Re-attach to an active cache job after page refresh / nav back
  useEffect(() => {
    if (!graphId) return;
    let cancelled = false;
    (async () => {
      try {
        const active = await graphBuilderApi.getActiveCacheJob(graphId);
        if (cancelled) return;
        if (active?.active && active?.job_id) {
          setCacheStatus('caching');
          setCacheJobId(active.job_id);
          setCachePhase(active.phase || 'running');
          setCachePercent(active.percent || 0);
          startCachePolling(active.job_id, Date.now());
        }
      } catch {
        // Silently ignore — re-attach is best-effort.
      }
    })();
    return () => {
      cancelled = true;
      stopCachePolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphId]);

  const handleDeleteCache = async () => {
    setDeletingCache(true);
    try {
      await graphBuilderApi.deleteCacheGraph(graphId);
      setCacheStatus('idle');
      setCacheInfo(null);
      onSettingsChange('use_cache', false);
      if (typeof onCacheStateChange === 'function') {
        onCacheStateChange();
      }
    } catch (err) {
      console.error('Delete cache failed:', err);
      setCacheError(err?.response?.data?.detail || err.message || 'Delete failed');
    } finally {
      setDeletingCache(false);
    }
  };

  const isCompiledMode = settings.snn_execution_mode === 'compiled_full' || settings.snn_execution_mode === 'compiled_full_numpy' || settings.snn_execution_mode === 'compiled_sharded';

  const handleSnnModeChange = async (newMode) => {
    // not_compiled needs no artifacts — always allowed
    if (newMode === 'not_compiled') {
      onSettingsChange('snn_execution_mode', newMode);
      setCompileModeWarning('');
      return;
    }

    // For compiled modes, verify artifacts exist
    try {
      const status = await graphBuilderApi.getCompileStatus(graphId);
      if ((newMode === 'compiled_full' || newMode === 'compiled_full_numpy') && !status.compiled_full) {
        setCompileModeWarning(
          'Compiled Full artifacts (NPZ) not found for this graph. Please compile the graph first from the Build & Tune menu before using this mode.'
        );
        return; // keep current selection
      }
      if (newMode === 'compiled_sharded' && !status.compiled_sharded) {
        setCompileModeWarning(
          'Compiled Sharded artifacts not found for this graph. Please compile the graph (sharded) first from the Build & Tune menu before using this mode.'
        );
        return; // keep current selection
      }
      // Artifacts exist — apply the change
      onSettingsChange('snn_execution_mode', newMode);
      setCompileModeWarning('');
    } catch (err) {
      // If we can't reach the status endpoint, allow the selection but warn
      console.warn('Could not verify compile status:', err);
      onSettingsChange('snn_execution_mode', newMode);
      setCompileModeWarning('');
    }
  };

  const handleVocabToggle = async (checked) => {
    if (checked) {
      // Check if vocabulary exists before enabling
      try {
        const result = await apiService.getGraphVocabulary(graphId);
        if (result.success && result.exists) {
          onSettingsChange('expand_with_vocabulary', true);
          setVocabWarning('');
        } else {
          setVocabWarning('No vocabulary context cached. Visit the Graph Stats page first to build it.');
          onSettingsChange('expand_with_vocabulary', false);
        }
      } catch (err) {
        setVocabWarning('Could not check vocabulary status. Visit Graph Stats page to build it.');
        onSettingsChange('expand_with_vocabulary', false);
      }
    } else {
      onSettingsChange('expand_with_vocabulary', false);
      setVocabWarning('');
    }
  };

  return (
    <div className="test-graph-settings">
      <div 
        className="settings-header"
        onClick={onToggleCollapse}
      >
        <div className="settings-title">
          <span className="settings-icon">{collapsed ? '▶' : '▼'}</span>
          <span>Pipeline Settings</span>
          <span className="settings-summary">
            {settings.model_preset === 'custom' ? 'Custom models' : (MODEL_PRESETS[settings.model_preset || 'balanced']?.label || settings.llm_model)} | {settings.snn_execution_mode} {settings.stream_response ? '| Streaming' : ''}
          </span>
        </div>
        <span className="settings-toggle">
          {collapsed ? 'Expand' : 'Collapse'}
        </span>
      </div>

      <div className={`settings-body ${collapsed ? 'collapsed' : ''}`}>
        {/* Row 1: IDs (read-only) */}
        <div className="settings-row">
          <div className="settings-group">
            <label>Graph ID</label>
            <input
              type="text"
              value={graphId || ''}
              readOnly
              className="readonly-input"
              title="Graph ID from URL - read only"
            />
          </div>
          <div className="settings-group">
            <label>Workspace ID</label>
            <input
              type="text"
              value={workspaceId || ''}
              readOnly
              className="readonly-input"
              title="Workspace ID from graph - read only"
            />
          </div>
        </div>

        {/* Phase 2 -- LLM Q1 query reformulation opt-in.
            Default OFF: structured / API / process callers skip the
            ~624 ms median (up to 7.9 s) gpt-4o-mini call. Flip ON for
            free-form user search where the question is messy or
            abbreviated. Surfaces enable_query_reformulation on every
            /pipeline/run payload. */}
        <div className="settings-row">
          <div className="settings-group" style={{ flex: 1 }}>
            <label htmlFor="enable_query_reformulation">Query Reformulation (Q1)</label>
            <select
              id="enable_query_reformulation"
              value={
                settings.enable_query_reformulation === true ? 'on' : 'off'
              }
              onChange={(e) =>
                onSettingsChange(
                  'enable_query_reformulation',
                  e.target.value === 'on'
                )
              }
            >
              <option value="off">Off (default) — fast; use for precise / structured inputs</option>
              <option value="on">On — adds ~600 ms; helps for free-form user search</option>
            </select>
            <span className="setting-hint">
              Per-request opt-in. When on, gpt-4o-mini rewrites the query into a
              tightened sentence before retrieval. Default off saves the LLM
              hop entirely (Q1 = Q0).
            </span>
          </div>
        </div>


        {/* Row 1b: Router Graph (optional) */}
        {routerGraphs.length > 0 && (
        <div className="settings-row">
          <div className="settings-group">
            <label htmlFor="router_graph_id">Router Graph (optional)</label>
            <select
              id="router_graph_id"
              value={settings.router_graph_id || ''}
              onChange={(e) => onSettingsChange('router_graph_id', e.target.value || null)}
            >
              <option value="">None (query this graph directly)</option>
              {routerGraphs.map(g => (
                <option key={g.graphId} value={g.graphId}>
                  {g.graphName}
                </option>
              ))}
            </select>
            <span className="setting-hint">Route queries through a Router graph first (~200ms overhead)</span>
          </div>
          {settings.router_graph_id && (
          <>
          <div className="settings-group">
            <label htmlFor="router_mode">Router Mode</label>
            <select
              id="router_mode"
              value={settings.router_mode || 'route_and_execute'}
              onChange={(e) => onSettingsChange('router_mode', e.target.value)}
            >
              <option value="route_and_execute">Route and Execute</option>
              <option value="route_only">Route Only (identify target graph)</option>
            </select>
          </div>
          <div className="settings-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!settings.multi_graph_enabled}
                onChange={(e) => onSettingsChange('multi_graph_enabled', e.target.checked)}
              />
              Multiple Graph Execution
            </label>
            {settings.multi_graph_enabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                <label htmlFor="multi_graph_threshold_pct" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                  Include graphs within
                </label>
                <input
                  id="multi_graph_threshold_pct"
                  type="number"
                  min="5"
                  max="80"
                  value={settings.multi_graph_threshold_pct ?? 30}
                  onChange={(e) => onSettingsChange('multi_graph_threshold_pct', parseInt(e.target.value) || 30)}
                  style={{ width: '55px', padding: '4px', borderRadius: '4px', border: '1px solid #d1d5db', textAlign: 'center' }}
                />
                <span style={{ fontSize: '12px' }}>% of top rank</span>
              </div>
            )}
            <span className="setting-hint">
              {settings.multi_graph_enabled
                ? `Executes against all graphs scoring within ${settings.multi_graph_threshold_pct ?? 30}% of the best match`
                : 'When enabled, queries run against multiple matching graphs in parallel'}
            </span>
          </div>
          </>
          )}
        </div>
        )}

        {/* Row 1c: Pre-Enrichment Process Maps */}
        {processMaps.length > 0 && (
        <div className="settings-row">
          <div className="settings-group" ref={processDropdownRef} style={{ position: 'relative' }}>
            <label>Pre-Enrichment Process Maps</label>
            <button
              type="button"
              className="graph-selector-toggle"
              onClick={() => setProcessDropdownOpen(!processDropdownOpen)}
              style={{ width: '100%', textAlign: 'left', padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '13px' }}
            >
              {(settings.pre_enrich_process_ids || []).length === 0
                ? 'None (no pre-enrichment)'
                : `${(settings.pre_enrich_process_ids || []).length} process map(s) selected`}
            </button>
            {processDropdownOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '220px', overflowY: 'auto', marginTop: '4px' }}>
                {processMaps.map(p => {
                  const pid = p.process_id || p.ProcessId;
                  const pname = p.name || p.Name || pid;
                  const selected = (settings.pre_enrich_process_ids || []).includes(pid);
                  return (
                    <label key={pid} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                          const current = settings.pre_enrich_process_ids || [];
                          const next = selected
                            ? current.filter(id => id !== pid)
                            : [...current, pid];
                          onSettingsChange('pre_enrich_process_ids', next);
                        }}
                      />
                      <span style={{ flex: 1 }}>{pname}</span>
                    </label>
                  );
                })}
              </div>
            )}
            <span className="setting-hint">Run process maps before graph queries to inject lookup context (medical policies, code enrichment)</span>
          </div>
          {(settings.pre_enrich_process_ids || []).length > 0 && (
          <div className="settings-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!settings.enable_nl_to_json}
                onChange={(e) => onSettingsChange('enable_nl_to_json', e.target.checked)}
              />
              NL-to-JSON Conversion
            </label>
            <span className="setting-hint">Convert natural language query to structured case JSON before process execution</span>
          </div>
          )}
        </div>
        )}

        {/* Row 2: Execution Modes */}
        <div className="settings-row">
          <div className="settings-group">
            <label htmlFor="execution_mode">Execution Mode</label>
            <select
              id="execution_mode"
              value={settings.execution_mode}
              onChange={(e) => onSettingsChange('execution_mode', e.target.value)}
            >
              {EXECUTION_MODES.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="settings-group">
            <label htmlFor="engine_mode">Engine Mode</label>
            <select
              id="snn_execution_mode"
              value={settings.snn_execution_mode}
              onChange={(e) => handleSnnModeChange(e.target.value)}
            >
              {ENGINE_MODES.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {compileModeWarning && (
              <span className="setting-warning">{compileModeWarning}</span>
            )}
            {isCompiledMode && (
              <div className="cache-controls-row" id="cache-controls-row">
                <label
                  className="cache-checkbox-label"
                  title="Probes Redis to confirm the graph is fully warm before each query (no inline warm). Click 'Cache Graph' first to populate the caches."
                >
                  <input
                    type="checkbox"
                    checked={!!settings.use_cache}
                    onChange={(e) => onSettingsChange('use_cache', e.target.checked)}
                  />
                  Require Warm Cache
                </label>
                <button
                  className={`cache-btn ${cacheStatus}`}
                  onClick={handleCacheGraph}
                  disabled={cacheStatus === 'caching'}
                  title="Compile, pre-load, and warm all cache layers for faster execution"
                >
                  {cacheStatus === 'idle' && 'Cache Graph'}
                  {cacheStatus === 'caching' && (
                    <>
                      <span className="cache-spinner" />
                      {cachePhase ? `${cachePhase} (${Math.round(cachePercent)}%)` : 'Caching...'}
                    </>
                  )}
                  {cacheStatus === 'cached' && (
                    <>Cached{cacheInfo?.elapsed_s ? ` (${cacheInfo.elapsed_s}s)` : ''}</>
                  )}
                  {cacheStatus === 'error' && 'Cache Failed'}
                </button>
                {cacheStatus === 'cached' && (
                  <button
                    className="cache-delete-btn"
                    onClick={handleDeleteCache}
                    disabled={deletingCache}
                    title="Delete all cached data for this graph"
                  >
                    {deletingCache ? 'Deleting...' : 'Delete Cache'}
                  </button>
                )}
                {cacheStatus === 'cached' && cacheInfo && (
                  <span className="cache-meta">
                    {cacheInfo.stats?.pool_neurons?.toLocaleString() || cacheInfo.stats?.compile_nodes?.toLocaleString() || cacheInfo.layers?.compile?.node_count?.toLocaleString() || '?'} nodes
                    {' · '}
                    {cacheInfo.stats?.pool_synapses?.toLocaleString() || cacheInfo.stats?.compile_synapses?.toLocaleString() || cacheInfo.layers?.compile?.synapse_count?.toLocaleString() || '?'} connections
                    {cacheInfo.stats?.synapse_embeddings === 'computed' && ' · embeddings ready'}
                  </span>
                )}
                {cacheStatus === 'error' && cacheError && (
                  <span className="setting-warning">{cacheError}</span>
                )}
              </div>
            )}
          </div>
          <div className="settings-group">
            <label htmlFor="llm_model">LLM Model (Fallback)</label>
            <select
              id="llm_model"
              value={settings.llm_model}
              onChange={(e) => onSettingsChange('llm_model', e.target.value)}
            >
              {LLM_MODEL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="settings-group">
            <label htmlFor="model_preset">Model Preset</label>
            <select
              id="model_preset"
              value={settings.model_preset || 'balanced'}
              onChange={(e) => {
                const preset = e.target.value;
                onSettingsChange('model_preset', preset);
                if (preset !== 'custom') {
                  onSettingsChange('stage_models', { ...MODEL_PRESETS[preset].models });
                  setStageModelsOpen(false);
                } else {
                  const current = settings.stage_models || MODEL_PRESETS.balanced.models;
                  onSettingsChange('stage_models', { ...current });
                  setStageModelsOpen(true);
                }
              }}
            >
              {Object.entries(MODEL_PRESETS).map(([key, preset]) => (
                <option key={key} value={key}>{preset.label} — {preset.description}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Per-stage model selectors (visible when Custom preset or expanded) */}
        {settings.stage_models && (
          <div className="stage-models-section">
            <div
              className="stage-models-header"
              onClick={() => setStageModelsOpen(!stageModelsOpen)}
            >
              <span className="settings-icon">{stageModelsOpen ? '▼' : '▶'}</span>
              <span className="stage-models-title">Per-Stage Model Selection</span>
              <span className="setting-hint" style={{ marginLeft: 8 }}>
                {settings.model_preset === 'custom' ? 'Custom configuration' : `Preset: ${MODEL_PRESETS[settings.model_preset || 'balanced']?.label || 'Balanced'}`}
              </span>
            </div>
            {stageModelsOpen && (
              <div className="stage-models-body">
                <div className="settings-row stage-models-grid">
                  {STAGE_MODEL_KEYS.map(({ key, label, hint }) => (
                    <div className="settings-group" key={key}>
                      <label htmlFor={`stage_model_${key}`}>{label}</label>
                      <select
                        id={`stage_model_${key}`}
                        value={(settings.stage_models || {})[key] || 'gpt-4o'}
                        onChange={(e) => {
                          const updated = { ...(settings.stage_models || {}), [key]: e.target.value };
                          onSettingsChange('stage_models', updated);
                          onSettingsChange('model_preset', 'custom');
                        }}
                      >
                        {LLM_MODEL_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <span className="setting-hint">{hint}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Row 3: Numeric Settings */}
        <div className="settings-row">
          <div className="settings-group">
            <label htmlFor="min_similarity">Min Similarity</label>
            <input
              id="min_similarity"
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={settings.min_similarity}
              onChange={(e) => onSettingsChange('min_similarity', parseFloat(e.target.value) || 0)}
            />
            <span className="setting-hint">0-1 threshold for semantic match</span>
          </div>
          <div className="settings-group">
            <label htmlFor="similarity_top_k">STK</label>
            <input
              id="similarity_top_k"
              type="number"
              min="1"
              max="500"
              value={settings.similarity_top_k}
              onChange={(e) => onSettingsChange('similarity_top_k', parseInt(e.target.value) || 150)}
            />
            <span className="setting-hint">Candidate nodes from search</span>
          </div>
          <div className="settings-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.decompose_query}
                onChange={(e) => onSettingsChange('decompose_query', e.target.checked)}
              />
              <span>Decompose Query</span>
            </label>
            <span className="setting-hint">Split compound queries</span>
          </div>
        </div>

        {/* Row 4: AQH Settings */}
        <div className="settings-row">
          <div className="settings-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.stream_response}
                onChange={(e) => onSettingsChange('stream_response', e.target.checked)}
              />
              <span>Stream Response</span>
            </label>
            <span className="setting-hint">Show tokens as they arrive</span>
          </div>
          <div className="settings-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.enable_abstract_query_handler}
                onChange={(e) => onSettingsChange('enable_abstract_query_handler', e.target.checked)}
              />
              <span>Enable AQH</span>
            </label>
            <span className="setting-hint">Abstract Query Handler</span>
          </div>
          <div className="settings-group">
            <label htmlFor="max_gap_fill_iterations">Gap Fill Iterations</label>
            <input
              id="max_gap_fill_iterations"
              type="number"
              min="0"
              max="5"
              value={settings.max_gap_fill_iterations}
              onChange={(e) => onSettingsChange('max_gap_fill_iterations', parseInt(e.target.value) || 0)}
              disabled={!settings.enable_abstract_query_handler}
            />
          </div>
          <div className="settings-group">
            <label htmlFor="graph_traversal_depth">TD</label>
            <input
              id="graph_traversal_depth"
              type="number"
              min="1"
              max="5"
              value={settings.graph_traversal_depth}
              onChange={(e) => onSettingsChange('graph_traversal_depth', parseInt(e.target.value) || 1)}
              disabled={!settings.enable_abstract_query_handler}
            />
          </div>
          <div className="settings-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.include_execution_trace}
                onChange={(e) => onSettingsChange('include_execution_trace', e.target.checked)}
              />
              <span>Include Execution Trace</span>
            </label>
            <span className="setting-hint">Show Platform debug info</span>
          </div>
          <div className="settings-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.expand_with_vocabulary}
                onChange={(e) => handleVocabToggle(e.target.checked)}
              />
              <span>VQE</span>
            </label>
            <span className="setting-hint">Expand query using graph vocabulary</span>
            {vocabWarning && (
              <span className="setting-warning">{vocabWarning}</span>
            )}
          </div>
        </div>

        {/* Row 5: NL Prompt */}
        <div className="settings-row full-width">
          <div className="settings-group">
            <label htmlFor="nl_prompt">Additional Context / Prompt (optional)</label>
            <textarea
              id="nl_prompt"
              value={settings.nl_prompt}
              onChange={(e) => onSettingsChange('nl_prompt', e.target.value)}
              placeholder="Optional: Provide additional context or instructions for the LLM response generation..."
              rows={3}
            />
            <span className="setting-hint">
              This text is prepended to conversation context and passed to the LLM
            </span>
          </div>
        </div>

        {/* Row 5-SEM: Focus Areas (Spiking Episodic Memory context modulation).
            Per-query dynamic goals that steer which remembered context the SNN
            re-activates. One focus area per line (or comma-separated). No-op
            unless SEM is enabled on the backend. */}
        <div className="settings-row full-width">
          <div className="settings-group">
            <label htmlFor="focus_areas">Focus Areas / Dynamic Goals (optional)</label>
            <textarea
              id="focus_areas"
              value={settings.focus_areas || ''}
              onChange={(e) => onSettingsChange('focus_areas', e.target.value)}
              placeholder={'One focus area per line, e.g.\nliquidity risk\nrelated party transactions'}
              rows={3}
            />
            <span className="setting-hint">
              Steers Spiking Episodic Memory (SEM) context activation for this query. One per line or comma-separated.
            </span>
          </div>
        </div>

        {/* Pipeline Tuning Preset — one-click apply of curated settings profiles */}
        <div className="settings-row">
          <div className="settings-group" style={{ flex: 1 }}>
            <label htmlFor="pipeline_tuning_preset">Pipeline Tuning Preset</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                id="pipeline_tuning_preset"
                value=""
                onChange={(e) => {
                  const key = e.target.value;
                  if (!key) return;
                  const preset = PIPELINE_TUNING_PRESETS[key];
                  if (!preset) return;
                  Object.entries(preset.settings).forEach(([k, v]) => {
                    onSettingsChange(k, v);
                  });
                }}
                style={{ flex: 1 }}
              >
                <option value="">Apply a preset...</option>
                {Object.entries(PIPELINE_TUNING_PRESETS).map(([key, preset]) => (
                  <option key={key} value={key}>{preset.label} — {preset.description}</option>
                ))}
              </select>
            </div>
            <span className="setting-hint">
              Applies curated settings for the selected profile. You can further customize individual settings after applying.
            </span>
          </div>
        </div>

        {/* Row 5a: FGE - Optimized (Compact Render) toggle.
            End-to-end A/B flag for the legacy Fast Graph Engine context
            bundle vs the new precision compact bundle. When ON the
            GraphBuilder switches Phase-8 to the render-only Llama prompt
            backed by claim_compact + SPO + synapse-fire weights, and
            auto-enables capture_synapse_fire_stats so the executor exports
            per-synapse firing stats that drive the compact potency score.
            Same graph, same retrieval -- only the LLM payload format and
            potency model change, so the toggle is a true A/B switch. */}
        <div className="settings-row fge-optimized-row">
          <div className="settings-group checkbox-group" style={{ flex: 1 }}>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.compact_render_mode === true}
                onChange={(e) => {
                  const on = e.target.checked;
                  onSettingsChange('compact_render_mode', on);
                  // Compact bundle's incoming-edge-strength scoring needs the
                  // executor to capture per-synapse firing stats. Auto-pair
                  // the two so the toggle works on its own.
                  onSettingsChange('capture_synapse_fire_stats', on);
                  // Compact bundle is its own distillation; the legacy
                  // distiller is bypassed inside Phase 8 when on.
                  if (on && settings.enable_context_distillation === true) {
                    onSettingsChange('enable_context_distillation', false);
                  }
                }}
              />
              <span>FGE Optimized (Compact Render)</span>
            </label>
            <span className="setting-hint">
              A/B switch. OFF = legacy FGE prioritized bundle. ON = compact
              evidence bundle (claim_compact + SPO + synapse-fire weights) +
              render-only Llama prompt. Auto-enables synapse fire-stat
              capture; bypasses the LLM context distiller.
            </span>
          </div>
        </div>

        {/* Row 5a-2: Compact Bundle Tuning (visible when compact render is ON) */}
        {settings.compact_render_mode === true && (
        <div className="settings-row">
          <div className="settings-group">
            <label htmlFor="compact_max_claims">Max Claims</label>
            <input
              id="compact_max_claims"
              type="number"
              min="1"
              max="200"
              step="1"
              value={settings.compact_max_claims ?? 100}
              onChange={(e) => onSettingsChange('compact_max_claims', parseInt(e.target.value) || 100)}
            />
            <span className="setting-hint">Hard ceiling on claims (default 100; token budget is the effective limiter)</span>
          </div>
          <div className="settings-group">
            <label htmlFor="compact_min_potency">Min Potency</label>
            <input
              id="compact_min_potency"
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={settings.compact_min_potency ?? 0.10}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val)) onSettingsChange('compact_min_potency', Math.min(1.0, Math.max(0, val)));
              }}
            />
            <span className="setting-hint">Min potency for compact bundle inclusion (default 0.10)</span>
          </div>
          <div className="settings-group">
            <label htmlFor="answer_max_tokens">Answer Max Tokens</label>
            <input
              id="answer_max_tokens"
              type="number"
              min="64"
              max="8192"
              step="64"
              value={settings.answer_max_tokens ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '' || raw === null) {
                  onSettingsChange('answer_max_tokens', null);
                  return;
                }
                const val = parseInt(raw);
                if (!isNaN(val)) onSettingsChange('answer_max_tokens', Math.min(8192, Math.max(64, val)));
              }}
              placeholder="Auto"
            />
            <span className="setting-hint">Empty = adaptive (1024 short / 4096 long). Range: 64–8192</span>
          </div>
        </div>
        )}

        {/* Row 5b: Context Bundle Settings */}
        <div className="settings-row">
          <div className="settings-group">
            <label htmlFor="context_potency_mode">CPT Mode</label>
            <select
              id="context_potency_mode"
              value={settings.context_potency_mode ?? 'auto'}
              onChange={(e) => onSettingsChange('context_potency_mode', e.target.value)}
            >
              {CONTEXT_POTENCY_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="setting-hint">
              Auto = backend picks per-query threshold (knee + p65 + top-K floor) and escalates if Judge flags low confidence
            </span>
          </div>
          {(settings.context_potency_mode ?? 'auto') === 'manual' && (
            <div className="settings-group">
              <label htmlFor="context_potency_threshold">CPT</label>
              <input
                id="context_potency_threshold"
                type="number"
                min="0.01"
                max="0.80"
                step="0.01"
                value={settings.context_potency_threshold ?? 0.10}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val)) onSettingsChange('context_potency_threshold', Math.min(0.80, Math.max(0.01, val)));
                }}
              />
              <span className="setting-hint">Range: 0.01–0.80</span>
            </div>
          )}
          {(settings.context_potency_mode ?? 'auto') === 'auto' && (
            <div className="settings-group">
              <label htmlFor="auto_potency_boost_multiplier">Boost</label>
              <input
                id="auto_potency_boost_multiplier"
                type="number"
                min="0.5"
                max="3.0"
                step="0.1"
                value={settings.auto_potency_boost_multiplier ?? 1.3}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val)) onSettingsChange(
                    'auto_potency_boost_multiplier',
                    Math.min(3.0, Math.max(0.5, val)),
                  );
                }}
                title="Multiplier applied to the auto-picked threshold (raw * boost). Judge fallback strips this back to raw."
              />
              <span className="setting-hint">Range: 0.5–3.0 (default 1.9)</span>
            </div>
          )}
          <div className="settings-group">
            <label htmlFor="verbatim_inclusion_mode">
              Backup tier (verbatim/reference)
            </label>
            <select
              id="verbatim_inclusion_mode"
              value={
                settings.verbatim_inclusion_mode
                ?? (settings.include_verbatim_nodes === false
                  ? 'force_exclude'
                  : settings.include_verbatim_nodes === true
                    ? 'force_include'
                    : 'auto')
              }
              onChange={(e) => {
                const mode = e.target.value;
                onSettingsChange('verbatim_inclusion_mode', mode);
                if (mode === 'force_include') {
                  onSettingsChange('include_verbatim_nodes', true);
                } else if (mode === 'force_exclude') {
                  onSettingsChange('include_verbatim_nodes', false);
                } else {
                  onSettingsChange('include_verbatim_nodes', null);
                }
              }}
            >
              <option value="auto">Auto (gate by coverage score)</option>
              <option value="force_include">Always include</option>
              <option value="force_exclude">Never include</option>
            </select>
            <span className="setting-hint">
              Auto = include backup nodes only when atomic-tier coverage is weak
            </span>
          </div>
          <div className="settings-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.enable_judge ?? true}
                onChange={(e) => onSettingsChange('enable_judge', e.target.checked)}
              />
              <span>Use Judge LLM</span>
            </label>
            <span className="setting-hint">Phase 9: evaluate and refine draft answer</span>
          </div>
          <div className="settings-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.enable_context_distillation ?? false}
                onChange={(e) => onSettingsChange('enable_context_distillation', e.target.checked)}
              />
              <span>CD</span>
            </label>
          </div>
          <div className="settings-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.enable_cross_encoder_rerank ?? false}
                onChange={(e) => onSettingsChange('enable_cross_encoder_rerank', e.target.checked)}
              />
              <span>CER</span>
            </label>
            <span className="setting-hint">(slower, higher precision)</span>
          </div>
        </div>

        {/* Row 5c: Seed Filtering (MLCF) */}
        <div className="snn-tuning-section">
          <div
            className="snn-tuning-header"
            onClick={() => setMlcfOpen(!mlcfOpen)}
          >
            <span className="settings-icon">{mlcfOpen ? '▼' : '▶'}</span>
            <span className="snn-tuning-title">MLCF</span>
          </div>

          {mlcfOpen && (
            <div className="snn-tuning-body">
              {/* Master toggle */}
              <div className="settings-row">
                <div className="settings-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.mlcf_enabled ?? true}
                      onChange={(e) => onSettingsChange('mlcf_enabled', e.target.checked)}
                    />
                    <span>Enable MLCF</span>
                  </label>
                  <span className="setting-hint">Master toggle for all MLCF filters</span>
                </div>
              </div>

              {/* Per-filter toggles */}
              {(settings.mlcf_enabled ?? true) && (
                <>
                  <div className="settings-row">
                    <div className="settings-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={settings.mlcf_identity_filter ?? true}
                          onChange={(e) => onSettingsChange('mlcf_identity_filter', e.target.checked)}
                        />
                        <span>Identity Mismatch Filter</span>
                      </label>
                      <span className="setting-hint">Prevent nodes with conflicting qualifiers (e.g., 6th house vs 8th house)</span>
                    </div>
                    <div className="settings-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={settings.mlcf_scope_filter ?? true}
                          onChange={(e) => onSettingsChange('mlcf_scope_filter', e.target.checked)}
                        />
                        <span>Scope Intersection Filter</span>
                      </label>
                      <span className="setting-hint">Kill nodes whose scope doesn't overlap the query</span>
                    </div>
                    <div className="settings-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={settings.mlcf_constraint_filter ?? true}
                          onChange={(e) => onSettingsChange('mlcf_constraint_filter', e.target.checked)}
                        />
                        <span>Constraint Satisfaction Filter</span>
                      </label>
                      <span className="setting-hint">Enforce edge-level constraint satisfaction</span>
                    </div>
                  </div>
                  <div className="settings-row">
                    <div className="settings-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={settings.mlcf_context_filter ?? true}
                          onChange={(e) => onSettingsChange('mlcf_context_filter', e.target.checked)}
                        />
                        <span>Context Compatibility Filter</span>
                      </label>
                      <span className="setting-hint">Ensure nodes share the same conceptual context layer</span>
                    </div>
                    <div className="settings-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={settings.mlcf_structural_filter ?? true}
                          onChange={(e) => onSettingsChange('mlcf_structural_filter', e.target.checked)}
                        />
                        <span>Structural Relation Validation</span>
                      </label>
                      <span className="setting-hint">Enforce valid node type pair relations</span>
                    </div>
                  </div>

                  {/* Thresholds */}
                  <div className="settings-row">
                    <div className="settings-group">
                      <label htmlFor="mlcf_identity_threshold">Identity Match Threshold</label>
                      <input
                        id="mlcf_identity_threshold"
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={settings.mlcf_identity_threshold ?? 0.5}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) onSettingsChange('mlcf_identity_threshold', Math.min(1.0, Math.max(0, val)));
                        }}
                      />
                      <span className="setting-hint">Default: 0.5 | Min qualifier overlap to pass</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="mlcf_scope_overlap_threshold">Scope Overlap Threshold</label>
                      <input
                        id="mlcf_scope_overlap_threshold"
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={settings.mlcf_scope_overlap_threshold ?? 0.3}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) onSettingsChange('mlcf_scope_overlap_threshold', Math.min(1.0, Math.max(0, val)));
                        }}
                      />
                      <span className="setting-hint">Default: 0.3 | Min scope tag overlap to pass</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="mlcf_context_strictness">Context Strictness</label>
                      <select
                        id="mlcf_context_strictness"
                        value={settings.mlcf_context_strictness ?? 'moderate'}
                        onChange={(e) => onSettingsChange('mlcf_context_strictness', e.target.value)}
                      >
                        <option value="strict">Strict</option>
                        <option value="moderate">Moderate</option>
                        <option value="permissive">Permissive</option>
                      </select>
                      <span className="setting-hint">How strictly context layers must match</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="mlcf_min_survivors">Min Survivors (Safety Valve)</label>
                      <input
                        type="range"
                        id="mlcf_min_survivors"
                        min="0"
                        max="10"
                        step="1"
                        value={settings.mlcf_min_survivors ?? 1}
                        onChange={(e) => onSettingsChange('mlcf_min_survivors', parseInt(e.target.value))}
                      />
                      <span className="setting-value">{settings.mlcf_min_survivors ?? 1}</span>
                      <span className="setting-hint">Default: 1 | If MLCF leaves fewer seeds than this, all originals are kept</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Row 5d: Synapse Activation (CRS) */}
        <div className="snn-tuning-section">
          <div
            className="snn-tuning-header"
            onClick={() => setCrsOpen(!crsOpen)}
          >
            <span className="settings-icon">{crsOpen ? '▼' : '▶'}</span>
            <span className="snn-tuning-title">CRS</span>
          </div>

          {crsOpen && (
            <div className="snn-tuning-body">
              {/* Master toggle */}
              <div className="settings-row">
                <div className="settings-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.crs_enabled ?? true}
                      onChange={(e) => onSettingsChange('crs_enabled', e.target.checked)}
                    />
                    <span>Enable Composite Relevance Scorer</span>
                  </label>
                </div>
              </div>

              {(settings.crs_enabled ?? true) && (
                <>
                  {/* Signal weights */}
                  <div className="settings-row">
                    <div className="settings-group">
                      <label htmlFor="crs_semantic_weight">Semantic Weight</label>
                      <input id="crs_semantic_weight" type="range" min="0" max="0.5" step="0.01"
                        value={settings.crs_semantic_weight ?? 0.25}
                        onChange={(e) => onSettingsChange('crs_semantic_weight', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_semantic_weight ?? 0.25).toFixed(2)}</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="crs_node_type_compat_weight">Node-Type Compat</label>
                      <input id="crs_node_type_compat_weight" type="range" min="0" max="0.5" step="0.01"
                        value={settings.crs_node_type_compat_weight ?? 0.15}
                        onChange={(e) => onSettingsChange('crs_node_type_compat_weight', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_node_type_compat_weight ?? 0.15).toFixed(2)}</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="crs_intent_affinity_weight">Intent Affinity</label>
                      <input id="crs_intent_affinity_weight" type="range" min="0" max="0.5" step="0.01"
                        value={settings.crs_intent_affinity_weight ?? 0.15}
                        onChange={(e) => onSettingsChange('crs_intent_affinity_weight', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_intent_affinity_weight ?? 0.15).toFixed(2)}</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="crs_scope_intersection_weight">Scope Intersection</label>
                      <input id="crs_scope_intersection_weight" type="range" min="0" max="0.5" step="0.01"
                        value={settings.crs_scope_intersection_weight ?? 0.15}
                        onChange={(e) => onSettingsChange('crs_scope_intersection_weight', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_scope_intersection_weight ?? 0.15).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="settings-row">
                    <div className="settings-group">
                      <label htmlFor="crs_causal_modality_weight">Causal / Modality</label>
                      <input id="crs_causal_modality_weight" type="range" min="0" max="0.5" step="0.01"
                        value={settings.crs_causal_modality_weight ?? 0.10}
                        onChange={(e) => onSettingsChange('crs_causal_modality_weight', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_causal_modality_weight ?? 0.10).toFixed(2)}</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="crs_authority_weight">Authority</label>
                      <input id="crs_authority_weight" type="range" min="0" max="0.5" step="0.01"
                        value={settings.crs_authority_weight ?? 0.08}
                        onChange={(e) => onSettingsChange('crs_authority_weight', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_authority_weight ?? 0.08).toFixed(2)}</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="crs_gate_readiness_weight">GR</label>
                      <input id="crs_gate_readiness_weight" type="range" min="0" max="0.5" step="0.01"
                        value={settings.crs_gate_readiness_weight ?? 0.07}
                        onChange={(e) => onSettingsChange('crs_gate_readiness_weight', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_gate_readiness_weight ?? 0.07).toFixed(2)}</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="crs_exception_alignment_weight">Exception Alignment</label>
                      <input id="crs_exception_alignment_weight" type="range" min="0" max="0.5" step="0.01"
                        value={settings.crs_exception_alignment_weight ?? 0.05}
                        onChange={(e) => onSettingsChange('crs_exception_alignment_weight', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_exception_alignment_weight ?? 0.05).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Thresholds */}
                  <div className="settings-row">
                    <div className="settings-group">
                      <label htmlFor="crs_evidence_threshold">Evidence Threshold</label>
                      <input id="crs_evidence_threshold" type="range" min="0.05" max="0.8" step="0.05"
                        value={settings.crs_evidence_threshold ?? 0.3}
                        onChange={(e) => onSettingsChange('crs_evidence_threshold', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_evidence_threshold ?? 0.3).toFixed(2)}</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="crs_default_unscored_relevance">Default Unscored Relevance</label>
                      <input id="crs_default_unscored_relevance" type="range" min="0.05" max="0.8" step="0.05"
                        value={settings.crs_default_unscored_relevance ?? 0.3}
                        onChange={(e) => onSettingsChange('crs_default_unscored_relevance', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.crs_default_unscored_relevance ?? 0.3).toFixed(2)} | Relevance for connections without CRS metadata</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Row 5e: Semantic Receptors */}
        <div className="snn-tuning-section">
          <div
            className="snn-tuning-header"
            onClick={() => setSerOpen(!serOpen)}
          >
            <span className="settings-icon">{serOpen ? '▼' : '▶'}</span>
            <span className="snn-tuning-title">SER</span>
          </div>

          {serOpen && (
            <div className="snn-tuning-body">
              <div className="settings-row">
                <div className="settings-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.enable_ser ?? false}
                      onChange={(e) => onSettingsChange('enable_ser', e.target.checked)}
                    />
                    <span>Enable SER Selection</span>
                  </label>
                </div>
              </div>

              {(settings.enable_ser ?? false) && (
                <>
                  <div className="settings-row">
                    <div className="settings-group">
                      <label htmlFor="ser_temperature">SER Temperature</label>
                      <input id="ser_temperature" type="range" min="0.05" max="2.0" step="0.05"
                        value={settings.ser_temperature ?? 0.35}
                        onChange={(e) => onSettingsChange('ser_temperature', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.ser_temperature ?? 0.35).toFixed(2)}</span>
                    </div>
                    <div className="settings-group">
                      <label htmlFor="ser_bm25_boost">ABB</label>
                      <input id="ser_bm25_boost" type="range" min="0" max="1.0" step="0.05"
                        value={settings.ser_bm25_boost ?? 0.1}
                        onChange={(e) => onSettingsChange('ser_bm25_boost', parseFloat(e.target.value))}
                      />
                      <span className="setting-hint">{(settings.ser_bm25_boost ?? 0.1).toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Row 6: Platform Tuning (only visible for compiled modes) */}
        {isCompiledMode && (
          <div className="snn-tuning-section">
            <div
              className="snn-tuning-header"
              onClick={() => setSnnTuningOpen(!snnTuningOpen)}
            >
              <span className="settings-icon">{snnTuningOpen ? '▼' : '▶'}</span>
              <span className="snn-tuning-title">Platform Tuning Parameters</span>
            </div>

            {snnTuningOpen && (
              <div className="snn-tuning-body">
                {/* Sub-row A: Simulation Hyperparameters */}
                <div className="settings-row">
                  <div className="settings-group">
                    <label htmlFor="simulate_duration_ms">SD</label>
                    <input
                      id="simulate_duration_ms"
                      type="number"
                      min="50"
                      max="2000"
                      step="10"
                      value={settings.simulate_duration_ms}
                      onChange={(e) => onSettingsChange('simulate_duration_ms', parseFloat(e.target.value) || 200)}
                    />
                    <span className="setting-hint">Default: 200 | Range: 50–2000</span>
                  </div>
                  <div className="settings-group">
                    <label htmlFor="simulate_step_ms">SS</label>
                    <input
                      id="simulate_step_ms"
                      type="number"
                      min="5"
                      max="200"
                      step="5"
                      value={settings.simulate_step_ms}
                      onChange={(e) => onSettingsChange('simulate_step_ms', parseFloat(e.target.value) || 20)}
                    />
                    <span className="setting-hint">Default: 20 | Range: 5–200</span>
                  </div>
                  <div className="settings-group">
                    <label htmlFor="max_recursive_ticks">MRT</label>
                    <input
                      id="max_recursive_ticks"
                      type="number"
                      min="1"
                      max="50"
                      step="1"
                      value={settings.max_recursive_ticks}
                      onChange={(e) => onSettingsChange('max_recursive_ticks', parseInt(e.target.value) || 10)}
                    />
                    <span className="setting-hint">Default: 10 | Range: 1–50</span>
                  </div>
                  <div className="settings-group">
                    <label htmlFor="bias_hz">BL</label>
                    <input
                      id="bias_hz"
                      type="number"
                      min="0"
                      max="200"
                      step="5"
                      value={settings.bias_hz}
                      onChange={(e) => onSettingsChange('bias_hz', parseFloat(e.target.value) || 0)}
                    />
                    <span className="setting-hint">Default: 50 | Range: 0–200</span>
                  </div>
                  <div className="settings-group">
                    <label htmlFor="gain">GNI</label>
                    <input
                      id="gain"
                      type="number"
                      min="1"
                      max="100"
                      step="1"
                      value={settings.gain}
                      onChange={(e) => onSettingsChange('gain', parseFloat(e.target.value) || 20)}
                    />
                    <span className="setting-hint">Default: 20 | Range: 1–100</span>
                  </div>
                  <div className="settings-group">
                    <label htmlFor="min_background_rate_hz">Min Background Level</label>
                    <input
                      id="min_background_rate_hz"
                      type="number"
                      min="-10"
                      max="10"
                      step="0.5"
                      value={settings.min_background_rate_hz}
                      onChange={(e) => onSettingsChange('min_background_rate_hz', parseFloat(e.target.value) || 0)}
                    />
                    <span className="setting-hint">Default: 0 | Range: -10–10</span>
                  </div>
                </div>

                {/* Sub-row B: Rate Coding Parameters */}
                <div className="settings-row">
                  <div className="settings-group">
                    <label htmlFor="rate_coding_min_hz">Scoring Min Measure</label>
                    <input
                      id="rate_coding_min_hz"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={settings.rate_coding_min_hz}
                      onChange={(e) => onSettingsChange('rate_coding_min_hz', parseFloat(e.target.value) || 0)}
                    />
                    <span className="setting-hint">Default: 8 | Range: 0–100</span>
                  </div>
                  <div className="settings-group">
                    <label htmlFor="rate_coding_max_hz">Scoring Max Measure</label>
                    <input
                      id="rate_coding_max_hz"
                      type="number"
                      min="50"
                      max="500"
                      step="10"
                      value={settings.rate_coding_max_hz}
                      onChange={(e) => onSettingsChange('rate_coding_max_hz', parseFloat(e.target.value) || 220)}
                    />
                    <span className="setting-hint">Default: 220 | Range: 50–500</span>
                  </div>
                  <div className="settings-group">
                    <label htmlFor="rate_coding_base_hz">Scoring Base Measure</label>
                    <input
                      id="rate_coding_base_hz"
                      type="number"
                      min="5"
                      max="200"
                      step="5"
                      value={settings.rate_coding_base_hz}
                      onChange={(e) => onSettingsChange('rate_coding_base_hz', parseFloat(e.target.value) || 50)}
                    />
                    <span className="setting-hint">Default: 50 | Range: 5–200</span>
                  </div>
                  <div className="settings-group">
                    <label htmlFor="softmax_temperature">ST</label>
                    <input
                      id="softmax_temperature"
                      type="number"
                      min="0.05"
                      max="2.0"
                      step="0.05"
                      value={settings.softmax_temperature}
                      onChange={(e) => onSettingsChange('softmax_temperature', parseFloat(e.target.value) || 0.35)}
                    />
                    <span className="setting-hint">Default: 0.35 | Range: 0.05–2.0</span>
                  </div>
                </div>

                {/* Sub-row C: Evidence Propagation */}
                <div className="settings-row">
                  <div className="settings-group">
                    <label htmlFor="propagation_evidence_threshold">PET</label>
                    <input id="propagation_evidence_threshold" type="range" min="0.01" max="0.5" step="0.01"
                      value={settings.propagation_evidence_threshold ?? 0.15}
                      onChange={(e) => onSettingsChange('propagation_evidence_threshold', parseFloat(e.target.value))}
                    />
                    <span className="setting-hint">{(settings.propagation_evidence_threshold ?? 0.15).toFixed(2)}</span>
                  </div>
                </div>

              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestGraphSettings;
